import { useCallback } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import type { SizeCalculator } from '@/features/layouts/components/Title/types';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { LogoOptions } from '@/types/gql.generated';

export const useCalcFontSize = (): SizeCalculator => {
  const { experience, readOnly } = usePageExperienceContext();
  const isMobileBreakpoint = useIsMobileBreakpoint();

  const logoEnabled = readOnly
    ? Boolean(experience?.logoImageUrl) &&
      experience?.logoDisplay === LogoOptions.Square
    : experience?.logoDisplay === LogoOptions.Square;

  return useCallback(
    (text: string) => {
      const minFontSize = 24;
      const maxFontSize = isMobileBreakpoint ? 32 : 42;
      const minTextLength = 0;
      const maxTextLength = logoEnabled ? 50 : 75;

      const length = text.length;

      if (length <= minTextLength) {
        return maxFontSize;
      }

      if (length >= maxTextLength) {
        return minFontSize;
      }

      // Calculate the font size based on a linear scale between min and max lengths
      const textRange = maxTextLength - minTextLength;
      const fontRange = maxFontSize - minFontSize;

      const scale = (length - minTextLength) / textRange;
      const fontSize = maxFontSize - scale * fontRange;

      return fontSize;
    },
    [logoEnabled, isMobileBreakpoint]
  );
};
